/**
 * Shared HUD UI primitives — layout wrappers only.
 *
 * Visual spec (from Figma node 2:2):
 *   - Pill container: border-radius 32px, white bg, shadow-overlay
 *   - Padding inside pill: 4px
 *   - Dividers: 1px vertical/horizontal separator
 *
 * Button / ButtonGroup come from @heroui/react — used directly in
 * consumer components (MainToolbar, InfoPill, etc.) instead of being
 * wrapped here, keeping the primitives file thin and transparent.
 */
import type { ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Pill container (white, rounded-full, shadow)
// ---------------------------------------------------------------------------

interface PillProps {
  children: ReactNode;
  className?: string;
  horizontal?: boolean;
}

/** 点击按钮后立即 blur，避免 Space/Enter 键盘事件被焦点按钮拦截 */
const blurActive = () => {
  if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
};

export function Pill({ children, className, horizontal = true }: PillProps) {
  // 首/尾直接子元素自动圆角：>=2 个按方向取首尾，仅 1 个则全圆角
  const autoRound = horizontal
    ? '[&>:only-child]:rounded-full [&>:first-child:not(:only-child)]:rounded-l-full [&>:last-child:not(:only-child)]:rounded-r-full'
    : '[&>:only-child]:rounded-full [&>:first-child:not(:only-child)]:rounded-t-full [&>:last-child:not(:only-child)]:rounded-b-full';

  return (
    <div
      onPointerUp={blurActive}
      className={[
        'flex rounded-[32px] bg-white p-1',
        horizontal ? 'flex-row items-center' : 'flex-col items-center',
        'shadow-[0px_14px_28px_rgba(0,0,0,0.08),0px_-6px_12px_rgba(0,0,0,0.03),0px_2px_8px_rgba(0,0,0,0.06)]',
        autoRound,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
}
